import { useEffect, useMemo } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { PlusCircle, Book, Search, Clock, Star, ChefHat, CalendarDays } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { DifficultyIndicator } from '@/components/recipe/difficulty-indicator'
import { useRecipeStore } from '@/stores/recipe-store'
import { useAuthStore } from '@/stores/auth-store'
import { useHouseholdStore } from '@/stores/household-store'

export default function HomePage() {
  const { recipes, getFilteredRecipes, loadRecipes } = useRecipeStore()
  const { user } = useAuthStore()
  const { currentHousehold, viewMode } = useHouseholdStore()
  const navigate = useNavigate()

  useEffect(() => {
    if (user) {
      void loadRecipes()
    }
  }, [loadRecipes, user])

  const visibleRecipes = useMemo(
    () => getFilteredRecipes(user?.id, currentHousehold?.id, viewMode),
    [currentHousehold?.id, getFilteredRecipes, recipes, user?.id, viewMode]
  )

  return (
    <div className="container py-8">
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">
          Welkom bij <span className="text-orange-500">KookInspiratie</span>
        </h1>
        <p className="mx-auto mb-8 max-w-3xl text-xl leading-relaxed text-gray-600">
          Beheer je recepten en plan samen jullie weekmenu zonder overbodige ruis.
        </p>

        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button size="lg" asChild>
            <Link to="/recepten/nieuw">
              <PlusCircle className="h-5 w-5 mr-2" />
              Nieuw Recept
            </Link>
          </Button>

          <Button variant="outline" size="lg" asChild>
            <Link to="/recepten">
              <Book className="h-5 w-5 mr-2" />
              Bekijk Recepten
            </Link>
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-12">
        <Card className="text-center hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate('/recepten')}>
          <CardHeader className="pb-3">
            <CardTitle className="text-2xl font-bold text-orange-500">{visibleRecipes.length}</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <p className="text-gray-600">Totaal Recepten</p>
          </CardContent>
        </Card>

        <Card className="text-center hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate('/recepten?filter=favoriet')}>
          <CardHeader className="pb-3">
            <CardTitle className="text-2xl font-bold text-red-500">
              {visibleRecipes.filter((recipe) => recipe.rating && recipe.rating >= 4).length}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <p className="text-gray-600">Favorieten</p>
          </CardContent>
        </Card>

        <Card className="text-center hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate('/weekplanning')}>
          <CardHeader className="pb-3">
            <CardTitle className="text-2xl font-bold text-blue-500">
              <CalendarDays className="h-8 w-8 mx-auto" />
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <p className="text-gray-600">Weekplanning</p>
          </CardContent>
        </Card>

        <Card className="text-center hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate('/recepten/nieuw')}>
          <CardHeader className="pb-3">
            <CardTitle className="text-2xl font-bold text-green-500">
              <PlusCircle className="h-8 w-8 mx-auto" />
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <p className="text-gray-600">Nieuw Recept</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
        <Card>
          <CardHeader>
            <CardTitle>Snelle Acties</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button asChild className="w-full justify-start">
              <Link to="/recepten/nieuw">
                <PlusCircle className="h-4 w-4 mr-2" />
                Nieuw Recept Toevoegen
              </Link>
            </Button>

            <Button variant="outline" asChild className="w-full justify-start">
              <Link to="/weekplanning">
                <CalendarDays className="h-4 w-4 mr-2" />
                Weekplanning Openen
              </Link>
            </Button>

            <Button variant="outline" asChild className="w-full justify-start">
              <Link to="/zoeken">
                <Search className="h-4 w-4 mr-2" />
                Recepten Zoeken
              </Link>
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Waar deze app voor is</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm text-gray-600">
            <p>Bewaar je eigen recepten overzichtelijk met tags, apparatuur en kooktijden.</p>
            <p>Plan diners voor de week en genereer daar direct een boodschappenlijst van.</p>
            <p>Werk vanuit persoonlijke recepten of een gedeeld huishouden met een kleine vriendengroep.</p>
          </CardContent>
        </Card>
      </div>

      <div className="mb-12">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Recente Recepten</h2>
          <Button variant="outline" asChild>
            <Link to="/recepten">Bekijk Alle</Link>
          </Button>
        </div>

        {visibleRecipes.length === 0 ? (
          <div className="text-center py-12">
            <ChefHat className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Nog geen recepten</h3>
            <p className="text-gray-600 mb-6">Begin met het toevoegen van je eerste recept.</p>
            <Button asChild>
              <Link to="/recepten/nieuw">
                <PlusCircle className="h-4 w-4 mr-2" />
                Eerste Recept Toevoegen
              </Link>
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {visibleRecipes.slice(0, 3).map((recipe) => (
              <Card key={recipe.id} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <CardTitle className="text-lg line-clamp-2">{recipe.title}</CardTitle>
                    {recipe.rating && (
                      <div className="flex items-center text-yellow-500 ml-2">
                        <Star className="h-4 w-4 fill-current" />
                        <span className="text-sm text-gray-600 ml-1">{recipe.rating}</span>
                      </div>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 text-sm mb-4 line-clamp-2">{recipe.description}</p>
                  <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
                    {(recipe.prep_time || recipe.cook_time) && (
                      <div className="flex items-center">
                        <Clock className="h-4 w-4 mr-1" />
                        {(recipe.prep_time || 0) + (recipe.cook_time || 0)} min
                      </div>
                    )}
                    <DifficultyIndicator difficulty={recipe.difficulty} showLabel={false} />
                  </div>
                  <Button size="sm" asChild className="w-full">
                    <Link to={`/recepten/${recipe.id}`}>Bekijk Recept</Link>
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
